import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFormAI } from "@/hooks/useFormAI";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileText, Trash2, CheckCircle2, ClipboardList, Wand2, Eye, Pencil, Link as LinkIcon, Shield, MessageSquare, CalendarDays, AlertTriangle, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { PROPOSAL_TEMPLATES } from "@/data/proposal-templates";
import { SYSTEM_FORM_TEMPLATES } from "@/data/system-forms";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { DigitalProposalTemplateEditor } from "@/components/proposals/DigitalProposalTemplateEditor";
import { Sparkles } from "lucide-react";

export default function Templates() {
  const navigate = useNavigate();
  const { user, orgId } = useAuth();
  const { generateForm, generating, prefillFormFromJob } = useFormAI();

  const [customTemplates, setCustomTemplates] = useState<any[]>([]);
  const [customForms, setCustomForms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("forms");
  const [formsSubTab, setFormsSubTab] = useState("system-forms");
  const [templatesSubTab, setTemplatesSubTab] = useState("system-templates");

  // AI Form Generation Dialog
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCategory, setAiCategory] = useState("inspection");

  // Fill Form Dialog
  const [fillFormOpen, setFillFormOpen] = useState(false);
  const [selectedFormTemplate, setSelectedFormTemplate] = useState<any | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [jobs, setJobs] = useState<any[]>([]);
  const [prefilledData, setPrefilledData] = useState<Record<string, any>>({});

  // Template preview dialog
  const [previewTemplate, setPreviewTemplate] = useState<any | null>(null);

  useEffect(() => {
    if (user && orgId) {
      loadCustomTemplates();
      loadCustomForms();
      loadJobs();
    }
  }, [user, orgId]);

  const loadCustomTemplates = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("proposal_templates")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCustomTemplates(data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const loadCustomForms = async () => {
    try {
      const { data, error } = await supabase
        .from("form_templates")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_system_template", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCustomForms(data || []);
    } catch (error) {
      console.error("Error loading forms:", error);
      toast.error("Failed to load forms");
    }
  };

  const loadJobs = async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from("jobs")
        .select("id, job_number, status, property_address, owner_name, property_city, property_state, property_zip")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error("Error loading jobs:", error);
    }
  };

  const handleUseTemplate = async (templateId: string) => {
    try {
      const template = PROPOSAL_TEMPLATES.find(t => t.id === templateId);
      if (!template) return;

      if (!orgId || !user) {
        toast.error("Please sign in to use templates");
        return;
      }

      const { data: newTemplate, error } = await supabase
        .from("proposal_templates")
        .insert([{
          name: template.name,
          description: template.description,
          doc_type: 'proposal',
          org_id: orgId,
          created_by: user.id,
          is_system_template: false,
          layout_json: template.layout as any,
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success("Template loaded successfully!");
      navigate(`/templates/builder/${newTemplate.id}`);
    } catch (error) {
      console.error("Error using template:", error);
      toast.error("Failed to load template");
    }
  };

  const handleDeleteCustomTemplate = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      const { error } = await supabase
        .from("proposal_templates")
        .delete()
        .eq("id", templateId);

      if (error) throw error;

      toast.success("Template deleted");
      loadCustomTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleEditCustomTemplate = (templateId: string) => {
    navigate(`/templates/builder/${templateId}`);
  };

  const handlePreviewSystemTemplate = (template: any) => {
    setPreviewTemplate({ ...template, isSystem: true });
  };

  const handlePreviewCustomTemplate = (template: any) => {
    setPreviewTemplate({
      ...template,
      isSystem: false,
      layout: template.layout_json,
    });
  };

  const handleEditSystemTemplate = async (templateId: string) => {
    // Clone to org, then open builder
    await handleUseTemplate(templateId);
  };

  const handleUseForm = async (formId: string) => {
    if (!orgId) {
      toast.error("Organization not found");
      return;
    }

    const systemTemplate = SYSTEM_FORM_TEMPLATES.find((f) => f.id === formId);
    const customTemplate = customForms.find((f) => f.id === formId);

    const template = systemTemplate || (customTemplate
      ? { ...customTemplate, fields: customTemplate.form_fields }
      : null);

    if (!template) {
      toast.error("Form not found");
      return;
    }

    setSelectedFormTemplate(template);
    setSelectedJobId("");
    setPrefilledData({});
    setFillFormOpen(true);
  };

  useEffect(() => {
    const prefill = async () => {
      if (!selectedFormTemplate || !selectedJobId) return;
      try {
        const data = await prefillFormFromJob(selectedJobId, selectedFormTemplate.fields);
        setPrefilledData(data || {});
      } catch (error) {
        console.error("Error prefilling form from job:", error);
      }
    };

    prefill();
  }, [selectedFormTemplate, selectedJobId, prefillFormFromJob]);

  const handleSubmitForm = async (formData: Record<string, any>) => {
    if (!selectedFormTemplate || !selectedJobId || !orgId) {
      toast.error("Please select a job before submitting");
      return;
    }

    try {
      const { error } = await supabase.from("form_submissions").insert({
        form_template_id: selectedFormTemplate.id,
        job_id: selectedJobId,
        org_id: orgId,
        submitted_data: formData,
        status: "submitted",
        submitted_at: new Date().toISOString(),
      });

      if (error) throw error;

      toast.success("Form submitted and attached to job");
      setFillFormOpen(false);
      setSelectedFormTemplate(null);
      setSelectedJobId("");
      setPrefilledData({});
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    }
  };

  const handleDeleteCustomForm = async (formId: string) => {
    if (!confirm("Are you sure you want to delete this form?")) return;

    try {
      const { error } = await supabase
        .from("form_templates")
        .delete()
        .eq("id", formId);

      if (error) throw error;

      toast.success("Form deleted");
      loadCustomForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      toast.error("Failed to delete form");
    }
  };

  const handleCreateCustomForm = () => {
    setAiDialogOpen(true);
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please describe the form you want to create");
      return;
    }

    if (!orgId) {
      toast.error("Organization ID not found");
      return;
    }

    const result = await generateForm(aiPrompt, orgId, aiCategory);
    if (result) {
      setAiDialogOpen(false);
      setAiPrompt("");
      setAiCategory("inspection");
      loadCustomForms();
      setActiveTab("forms");
      setFormsSubTab("custom-forms");
    }
  };

  const filteredSystemTemplates = PROPOSAL_TEMPLATES.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const filteredCustomTemplates = customTemplates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const filteredSystemForms = SYSTEM_FORM_TEMPLATES.filter((form) =>
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCustomForms = customForms.filter((form) =>
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      inspection: "bg-blue-600 text-white border-transparent hover:bg-blue-600",
      compliance: "bg-purple-600 text-white border-transparent hover:bg-purple-600",
      customer: "bg-emerald-600 text-white border-transparent hover:bg-emerald-600",
      production: "bg-orange-600 text-white border-transparent hover:bg-orange-600",
      insurance: "bg-red-600 text-white border-transparent hover:bg-red-600",
    };
    return colors[category] || "bg-slate-700 text-white border-transparent hover:bg-slate-700";
  };

  const getFormIconClasses = (category?: string) => {
    const styles: Record<string, string> = {
      inspection: "border-blue-600/40 bg-blue-600 text-white shadow-sm",
      compliance: "border-purple-600/40 bg-purple-600 text-white shadow-sm",
      customer: "border-emerald-600/40 bg-emerald-600 text-white shadow-sm",
      production: "border-orange-600/40 bg-orange-600 text-white shadow-sm",
      insurance: "border-red-600/40 bg-red-600 text-white shadow-sm",
    };

    return styles[category || ""] || "border-border bg-primary text-primary-foreground shadow-sm";
  };

  const getFormIcon = (form: any) => {
    const content = `${form?.name || ""} ${form?.description || ""}`.toLowerCase();

    if (content.includes("quality")) return CheckCircle2;
    if (content.includes("condition") || content.includes("assessment")) return Eye;
    if (content.includes("permit") || content.includes("lien") || content.includes("cancel") || content.includes("waiver")) return FileSignature;
    if (content.includes("survey") || content.includes("customer") || content.includes("feedback")) return MessageSquare;
    if (content.includes("daily") || content.includes("progress") || content.includes("report")) return CalendarDays;
    if (content.includes("safety")) return Shield;
    if (content.includes("damage") || content.includes("adjuster") || content.includes("claim") || content.includes("insurance")) return AlertTriangle;
    if (content.includes("inspection") || content.includes("roof")) return Search;

    switch (form?.category) {
      case "inspection":
        return Search;
      case "compliance":
        return FileSignature;
      case "customer":
        return MessageSquare;
      case "production":
        return CalendarDays;
      case "insurance":
        return AlertTriangle;
      default:
        return FileText;
    }
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Forms & Templates</h1>
        <p className="text-muted-foreground">
          Manage job forms and proposal templates
        </p>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Main Tabs - Forms vs Templates */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-11 p-1 bg-muted/60 border border-border rounded-xl">
          <TabsTrigger value="forms" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">Forms</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">PDF Templates</TabsTrigger>
          <TabsTrigger value="digital" className="gap-1 rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">
            <Sparkles className="w-3.5 h-3.5" /> Digital Proposal
          </TabsTrigger>
        </TabsList>

        {/* FORMS TAB */}
        <TabsContent value="forms" className="mt-6">
          <Tabs value={formsSubTab} onValueChange={setFormsSubTab}>
            <TabsList className="h-11 p-1 bg-muted/60 border border-border rounded-xl">
              <TabsTrigger value="system-forms" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">Form Library</TabsTrigger>
              <TabsTrigger value="custom-forms" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">My Custom Forms</TabsTrigger>
            </TabsList>

            {/* System Forms */}
            <TabsContent value="system-forms" className="mt-6">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Industry-Standard Forms</h3>
                  <p className="text-sm text-muted-foreground">
                    Pre-built forms for inspections, compliance, and customer feedback
                  </p>
                </div>
                <Button onClick={handleCreateCustomForm} className="gap-2">
                  <Wand2 className="w-4 h-4" />
                  Create Custom Form
                </Button>
              </div>

              {filteredSystemForms.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-2">No forms found</p>
                    <p className="text-sm text-muted-foreground">Try adjusting your search</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSystemForms.map((form) => {
                    const FormIcon = getFormIcon(form);

                    return (
                    <Card key={form.id} className="hover:shadow-lg transition-all hover:border-primary/50">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-4">
                          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${getFormIconClasses(form.category)}`}>
                            <FormIcon className="h-7 w-7" strokeWidth={2.25} />
                          </div>
                          <Badge className={getCategoryColor(form.category)}>
                            {form.category}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl">{form.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {form.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                            <span>{form.fields.length} fields</span>
                          </div>
                          <Button
                            className="w-full font-semibold shadow-sm"
                            onClick={() => handleUseForm(form.id)}
                          >
                            Use This Form
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </TabsContent>

            {/* Custom Forms */}
            <TabsContent value="custom-forms" className="mt-6">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Your Custom Forms</h3>
                  <p className="text-sm text-muted-foreground">
                    Forms you've created for your organization
                  </p>
                </div>
                <Button onClick={handleCreateCustomForm} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Form
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Loading custom forms...</p>
                </div>
              ) : filteredCustomForms.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-2">No custom forms yet</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create custom forms with our AI-powered form builder
                    </p>
                    <Button onClick={handleCreateCustomForm} className="gap-2">
                      <Wand2 className="w-4 h-4" />
                      Create Your First Form
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCustomForms.map((form) => {
                    const FormIcon = getFormIcon(form);

                    return (
                    <Card key={form.id} className="hover:shadow-md transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${getFormIconClasses(form.category)}`}>
                            <FormIcon className="h-6 w-6" strokeWidth={2.25} />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDeleteCustomForm(form.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <CardTitle className="mt-4">{form.name}</CardTitle>
                        <CardDescription>
                          {form.description || "No description"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          {form.updated_at
                            ? `Last updated: ${new Date(form.updated_at).toLocaleDateString()}`
                            : ""}
                        </p>
                        <Button
                          className="w-full font-semibold shadow-sm"
                          onClick={() => handleUseForm(form.id)}
                        >
                          Use This Form
                        </Button>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="mt-6">
          <Tabs value={templatesSubTab} onValueChange={setTemplatesSubTab}>
            <TabsList className="h-11 p-1 bg-muted/60 border border-border rounded-xl">
              <TabsTrigger value="system-templates" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">Pre-Built Templates</TabsTrigger>
              <TabsTrigger value="custom-templates" className="rounded-lg px-4 py-2 font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">My Custom Templates</TabsTrigger>
            </TabsList>

            {/* System Templates */}
            <TabsContent value="system-templates" className="mt-6">
              {filteredSystemTemplates.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-2">No templates found</p>
                    <p className="text-sm text-muted-foreground">Try adjusting your search</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSystemTemplates.map((template) => {
                    const primaryColor = template.layout.styles.primaryColor;
                    const accentColor = template.layout.styles.accent || primaryColor;
                    const fontFamily = template.layout.styles.fontFamily || 'Inter';
                    
                    return (
                      <Card 
                        key={template.id} 
                        className="hover:shadow-xl transition-all overflow-hidden group relative"
                        style={{
                          borderColor: `${primaryColor}20`,
                        }}
                      >
                        <div 
                          className="h-32 relative overflow-hidden"
                          style={{
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                          }}
                        >
                          <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl bg-white" />
                            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full blur-2xl bg-white" />
                          </div>
                          <div className="relative z-10 p-6 flex items-start justify-between h-full">
                            <div className="p-2.5 rounded-lg bg-white/20 backdrop-blur-sm">
                              <FileText className="w-7 h-7 text-white" />
                            </div>
                            <Badge 
                              variant="secondary" 
                              className="capitalize bg-white text-slate-900 border-0 font-semibold shadow-sm"
                            >
                              {template.category}
                            </Badge>
                          </div>
                        </div>

                        <CardHeader className="pb-3">
                          <CardTitle 
                            className="text-xl mb-2"
                            style={{ 
                              fontFamily: fontFamily,
                              color: primaryColor 
                            }}
                          >
                            {template.name}
                          </CardTitle>
                          <CardDescription className="text-sm leading-relaxed">
                            {template.description}
                          </CardDescription>
                        </CardHeader>
                        
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                              <CheckCircle2 
                                className="w-4 h-4" 
                                strokeWidth={2.5}
                                style={{ color: primaryColor }}
                              />
                              <span>{template.layout.pages.length} pages • {fontFamily}</span>
                            </div>
                            <Button
                              className="w-full transition-all"
                              onClick={() => handleUseTemplate(template.id)}
                              style={{
                                backgroundColor: primaryColor,
                                color: 'white',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = accentColor;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = primaryColor;
                              }}
                            >
                              Use This Template
                            </Button>
                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreviewSystemTemplate(template)}
                                className="gap-1"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                Preview
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSystemTemplate(template.id)}
                                className="gap-1"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Custom Templates */}
            <TabsContent value="custom-templates" className="mt-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Loading custom templates...</p>
                </div>
              ) : filteredCustomTemplates.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-2">No custom templates yet</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create a custom template by modifying a pre-built one
                    </p>
                    <Button onClick={() => setTemplatesSubTab("system-templates")}>
                      Browse Pre-Built Templates
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCustomTemplates.map((template) => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <FileText className="w-8 h-8 text-primary" />
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteCustomTemplate(template.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <CardTitle className="mt-4">{template.name}</CardTitle>
                        <CardDescription>
                          {template.description || "No description"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          Last updated: {new Date(template.updated_at).toLocaleDateString()}
                        </p>
                        <div className="flex flex-col gap-2">
                          <Button
                            className="w-full"
                            onClick={() => handleEditCustomTemplate(template.id)}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Open in Builder
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => handlePreviewCustomTemplate(template)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* DIGITAL PROPOSAL TAB */}
        <TabsContent value="digital" className="mt-6">
          <DigitalProposalTemplateEditor />
        </TabsContent>
      </Tabs>

      {/* AI Form Generation Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Form with AI</DialogTitle>
            <DialogDescription>
              Describe the form you want to create, and AI will generate it for you
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Form Category</Label>
              <Select value={aiCategory} onValueChange={setAiCategory}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Describe Your Form</Label>
              <Textarea
                placeholder="E.g., 'Create a form to track equipment maintenance with fields for equipment name, last service date, next service date, and technician notes'"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={5}
                className="mt-2"
              />
            </div>
            <Button
              onClick={handleAIGenerate}
              disabled={generating || !aiPrompt.trim()}
              className="w-full"
            >
              {generating ? "Generating..." : "Generate Form"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fill Form Dialog */}
      <Dialog open={fillFormOpen} onOpenChange={setFillFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedFormTemplate ? selectedFormTemplate.name : "Use Form"}
            </DialogTitle>
            <DialogDescription>
              Select a job to attach this form to, then fill it out.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-100px)] pr-4">
            <div className="space-y-4">
              <div>
                <Label>Attach to Job</Label>
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a job">
                      {selectedJobId && jobs.find(j => j.id === selectedJobId) && (() => {
                        const job = jobs.find(j => j.id === selectedJobId)!;
                        const displayName = job.owner_name || "Unknown Customer";
                        const displayAddress = job.property_address 
                          ? `${job.property_address}${job.property_city ? `, ${job.property_city}` : ''}${job.property_state ? `, ${job.property_state}` : ''}`
                          : "No address";
                        return `${displayName} · ${displayAddress}`;
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => {
                      const displayName = job.owner_name || "Unknown Customer";
                      const displayAddress = job.property_address 
                        ? `${job.property_address}${job.property_city ? `, ${job.property_city}` : ''}${job.property_state ? `, ${job.property_state}` : ''}`
                        : "No address";
                      const jobNumber = job.job_number || job.id.substring(0, 8);
                      
                      return (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex flex-col items-start py-1">
                            <div className="font-medium">{displayName} · {displayAddress}</div>
                            <div className="text-xs text-muted-foreground">
                              Job: {jobNumber} {job.status ? `· ${job.status}` : ""}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedFormTemplate && (
                <FormRenderer
                  formName={selectedFormTemplate.name}
                  formDescription={selectedFormTemplate.description}
                  fields={selectedFormTemplate.fields}
                  initialData={prefilledData}
                  onSubmit={handleSubmitForm}
                />
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(o) => !o && setPreviewTemplate(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              {previewTemplate?.description || "Preview of the proposal template"}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-120px)] px-6 pb-6">
            {previewTemplate?.layout?.pages?.map((page: any, i: number) => (
              <div
                key={page.id || i}
                className="bg-white rounded-lg shadow-md mb-6 p-10 min-h-[600px]"
                style={{
                  backgroundColor: page.pageSettings?.backgroundColor || '#ffffff',
                }}
              >
                <div className="text-xs text-muted-foreground mb-4">
                  Page {i + 1} — {page.title || "Untitled"}
                </div>
                <div className="space-y-4">
                  {(page.blocks || [])
                    .slice()
                    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                    .map((block: any) => (
                      <div
                        key={block.id}
                        className="border border-dashed border-muted rounded p-3 text-sm"
                      >
                        <div className="font-medium text-muted-foreground capitalize mb-1">
                          {block.type?.replace(/_/g, ' ')}
                        </div>
                        {block.content?.text && (
                          <div
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: block.content.text }}
                          />
                        )}
                        {block.content?.title && (
                          <div className="font-semibold">{block.content.title}</div>
                        )}
                        {block.content?.subtitle && (
                          <div className="text-muted-foreground">{block.content.subtitle}</div>
                        )}
                        {block.content?.imageUrl && (
                          <img
                            src={block.content.imageUrl}
                            alt=""
                            className="mt-2 max-h-48 rounded"
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
            <div className="flex justify-end gap-2 sticky bottom-0 bg-background py-3 border-t">
              {previewTemplate?.isSystem ? (
                <Button onClick={() => {
                  const id = previewTemplate.id;
                  setPreviewTemplate(null);
                  handleUseTemplate(id);
                }}>
                  Use This Template
                </Button>
              ) : (
                <Button onClick={() => {
                  const id = previewTemplate.id;
                  setPreviewTemplate(null);
                  navigate(`/templates/builder/${id}`);
                }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Open in Builder
                </Button>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
